import React, { useState, useEffect } from 'react';
import { trackValueDashboardViewed } from '../utils/analytics';
import {
  Layout,
  Row,
  Col,
  Card,
  Typography,
  Statistic,
  Progress,
  Alert,
  List,
  Tag,
  Spin,
  Button,
  Select,
} from 'antd';
import {
  DollarOutlined,
  ThunderboltOutlined,
  EnvironmentOutlined,
  TrophyOutlined,
  BulbOutlined,
  CalendarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from '../contexts/LocationContext';
import DataFreshnessIndicator from '../components/DataFreshnessIndicator';
import { ExportMenu } from '../components/ValueDashboard/ExportMenu';
import axios from 'axios';
import { config } from '../config/env';
import { savingsReasonText } from '../components/InsufficientDataCard';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

interface SavingsMetrics {
  /** P1 M&V contract: fields are null when not computable from metered records */
  kWhReduced: number | null;
  kWShifted: number | null;
  dollarsSaved: number | null;
  co2Reduced: number | null;
  paybackMonths: number | null;
  roi: number | null;
  location: {
    lat: number;
    lng: number;
    utility?: string;
    tariffId?: string;
  } | null;
  lastUpdated?: string;
  source?: string;
}

interface LocationInsights {
  /** P1 M&V contract: 'insufficient_data' + reason when analysis cannot run */
  status?: 'ok' | 'insufficient_data';
  reason?: string;
  location: {
    lat: number;
    lng: number;
    city?: string;
    state?: string;
    utility?: string;
  } | null;
  energyInsights: EnergyInsight[];
  recommendations: string[];
  totalPotentialSavings: number | null;
  optimizationScore: number | null;
  lastUpdated?: string;
  source?: string;
}

interface EnergyInsight {
  type: string;
  title: string;
  description: string;
  value: number;
  unit: string;
  recommendation: string;
  priority: 'high' | 'medium' | 'low';
  potentialSavings?: number;
}

interface LocationInsights {
  status?: 'ok' | 'insufficient_data';
  reason?: string;
  location: {
    lat: number;
    lng: number;
    city?: string;
    state?: string;
    utility?: string;
  } | null;
  energyInsights: EnergyInsight[];
  recommendations: string[];
  totalPotentialSavings: number | null;
  optimizationScore: number | null;
}

export const ValueDashboard: React.FC = () => {
  const { user } = useAuth();
  const { city, state, utility } = useLocation();
  const [savings, setSavings] = useState<SavingsMetrics | null>(null);
  const [insights, setInsights] = useState<LocationInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [error, setError] = useState<string | null>(null);
  const [savingsUnavailable, setSavingsUnavailable] = useState<string | null>(
    null,
  );

  // Define isSubscribed before useEffect uses it
  const isSubscribed = user?.isSubscribed || false;

  const fetchData = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        setLoading(false);
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };

      const [savingsResponse, insightsResponse] = await Promise.all([
        axios.get(`${config.apiUrl}/api/energy/savings/metrics?days=${period}`, { headers }),
        axios.get(`${config.apiUrl}/api/insights/location`, { headers }),
      ]);

      // P1 M&V: metrics are only surfaced when the backend vouched for them.
      const savingsPayload = savingsResponse.data?.savings;
      const insightsData = insightsResponse.data?.insights || insightsResponse.data;

      const metrics =
        savingsPayload &&
        savingsPayload.status === 'ok' &&
        savingsPayload.metrics
          ? (savingsPayload.metrics as SavingsMetrics)
          : null;

      setSavings(metrics);
      setSavingsUnavailable(
        savingsPayload && savingsPayload.status !== 'ok'
          ? savingsReasonText(savingsPayload)
          : null,
      );
      setInsights(insightsData || null);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch value data:', err);
      
      // Provide more specific error messages
      if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
      } else if (err.response?.status === 403) {
        setError('Access denied. Please check your subscription status.');
      } else if (err.response?.status >= 500) {
        setError('Server error. Please try again in a few moments.');
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Failed to load value metrics. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Track page view
    trackValueDashboardViewed(isSubscribed);
  }, [user?.id, period, isSubscribed]);

  if (loading) {
    return (
      <Spin
        size="large"
        style={{ display: 'block', margin: 'auto', marginTop: '100px' }}
        tip="Loading value metrics..."
      />
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={fetchData}>
            Retry
          </Button>
        }
      />
    );
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'red';
      case 'medium': return 'orange';
      case 'low': return 'green';
      default: return 'blue';
    }
  };

  const formatCurrency = (amount: number) => 
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  /** P1 M&V: absent numbers render as absent — never coerced to 0. */
  const displayValue = (v: number | null | undefined): number | string =>
    typeof v === 'number' ? v : '—';

  const annualizedSavings =
    savings?.dollarsSaved != null
      ? savings.dollarsSaved * (365 / parseInt(period))
      : null;
  const optScore = insights?.optimizationScore ?? null;
  // Teaser mode for free users
  if (!isSubscribed) {
    // Honest teaser: only real metered figures may be shown —
    // no ×1.5 inflation and no default $150.
    const teaserSavings = savings?.dollarsSaved ?? null;
    
    return (
      <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
        <Content style={{ padding: '24px' }}>
          {!savings && savingsUnavailable && (
            <Alert
              message="No verified savings figures yet"
              description={savingsUnavailable}
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />
          )}
          <Alert
            message="Unlock Full Value Dashboard"
            description={
              teaserSavings !== null
                ? `You're viewing a preview. Your metered savings: ${formatCurrency(teaserSavings)}/month. Unlock complete analytics, historical data, and optimization recommendations.`
                : "You're viewing a preview. Verified savings figures appear once metered data is available — nothing here is estimated. Unlock complete analytics, historical data, and optimization recommendations."
            }
            type="info"
            showIcon
            action={
              <Button type="primary" onClick={() => window.location.href = '/expedient'}>
                Upgrade Now
              </Button>
            }
            style={{ marginBottom: '24px' }}
          />
          
          <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Title level={2} style={{ margin: 0 }}>
                Value Dashboard <span style={{ fontSize: '14px', color: '#999' }}>(Preview)</span>
              </Title>
            <Text type="secondary">
              {city && state ? `${city}, ${state}` : 'Your Location'} • {utility || 'Local Utility'}
              {savings?.lastUpdated && (
                <span style={{ marginLeft: '8px' }}>
                  <DataFreshnessIndicator 
                    lastUpdated={savings.lastUpdated} 
                    source={savings.source}
                    staleThresholdMinutes={60}
                  />
                </span>
              )}
            </Text>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <Select
              value={period}
              onChange={setPeriod}
              style={{ width: 120 }}
              placeholder="Period"
            >
              <Option value="7">7 days</Option>
              <Option value="30">30 days</Option>
              <Option value="90">90 days</Option>
            </Select>
            {isSubscribed && <ExportMenu days={parseInt(period)} />}
          </div>
        </div>

        <Row gutter={[24, 24]}>
          {/* Key Metrics */}
          <Col span={24}>
            <Card title="💰 Financial Impact" style={{ marginBottom: '24px' }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Total Savings"
                    value={displayValue(savings?.dollarsSaved)}
                    prefix={<DollarOutlined />}
                    suffix={`/ ${period} days`}
                    valueStyle={{ color: '#3f8600' }}
                    formatter={(value) => (typeof value === 'number' ? formatCurrency(value) : '—')}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Annual Projection"
                    value={displayValue(annualizedSavings)}
                    prefix={<ArrowUpOutlined />}
                    valueStyle={{ color: '#3f8600' }}
                    formatter={(value) => (typeof value === 'number' ? formatCurrency(value) : '—')}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="ROI"
                    value={displayValue(savings?.roi)}
                    suffix="%"
                    prefix={<TrophyOutlined />}
                    valueStyle={{ color: savings?.roi && savings.roi > 100 ? '#3f8600' : '#1890ff' }}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Payback Period"
                    value={displayValue(savings?.paybackMonths)}
                    suffix="months"
                    prefix={<CalendarOutlined />}
                    valueStyle={{ color: savings?.paybackMonths && savings.paybackMonths < 12 ? '#3f8600' : '#1890ff' }}
                  />
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Energy Metrics */}
          <Col xs={24} lg={12}>
            <Card title="⚡ Energy Impact">
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Statistic
                    title="Energy Reduced"
                    value={displayValue(savings?.kWhReduced)}
                    suffix="kWh"
                    prefix={<ThunderboltOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Peak Shifted"
                    value={displayValue(savings?.kWShifted)}
                    suffix="kW"
                    prefix={<ArrowDownOutlined />}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Col>
                <Col span={24}>
                  <Statistic
                    title="CO₂ Reduced"
                    value={displayValue(savings?.co2Reduced)}
                    suffix="tCO₂e"
                    prefix={<EnvironmentOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Optimization Score */}
          <Col xs={24} lg={12}>
            <Card title="📊 Optimization Score">
              <div style={{ textAlign: 'center' }}>
                <Progress
                  type="circle"
                  percent={optScore ?? 0}
                  size={120}
                  strokeColor={
                    (optScore ?? -1) > 80
                      ? '#52c41a'
                      : (optScore ?? -1) > 60
                      ? '#1890ff'
                      : '#faad14'
                  }
                />
                <div style={{ marginTop: '16px' }}>
                  <Text strong>Overall Performance</Text>
                  <br />
                  <Text type="secondary">
                    {(optScore ?? -1) > 80
                      ? 'Excellent optimization'
                      : (optScore ?? -1) > 60
                      ? 'Good performance'
                      : optScore === null ? 'Awaiting metered data' : 'Room for improvement'}
                  </Text>
                </div>
              </div>
            </Card>
          </Col>

          {/* Energy Insights */}
          <Col xs={24} lg={12}>
            <Card title="⚡ Energy Insights">
              <List
                dataSource={insights?.energyInsights || []}
                renderItem={(insight) => (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>{insight.title}</span>
                          <Tag color={getPriorityColor(insight.priority)}>
                            {insight.priority.toUpperCase()}
                          </Tag>
                        </div>
                      }
                      description={
                        <div>
                          <div>{insight.description}</div>
                          <Text strong style={{ color: '#1890ff' }}>
                            {insight.value} {insight.unit}
                          </Text>
                          {insight.potentialSavings && (
                            <div style={{ marginTop: '4px' }}>
                              <Text type="success">
                                💰 Potential: {formatCurrency(insight.potentialSavings)}/month
                              </Text>
                            </div>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>

          {/* Recommendations */}
          <Col xs={24} lg={12}>
            <Card 
              title="🎯 Action Items" 
              extra={
                <Text type="secondary">
                  Potential: {insights?.totalPotentialSavings != null ? formatCurrency(insights.totalPotentialSavings) + '/month' : '—'}
                </Text>
              }
            >
              <List
                dataSource={insights?.recommendations || []}
                renderItem={(recommendation) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<BulbOutlined style={{ color: '#faad14' }} />}
                      description={recommendation}
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>

          {/* Location Context */}
          <Col span={24}>
            <Alert
              message={`Location-Optimized for ${city || 'Your Area'}`}
              description={`Preview mode: See potential savings calculations. Upgrade to unlock full analytics, historical trends, and actionable recommendations.`}
              type="warning"
              showIcon
              icon={<EnvironmentOutlined />}
              action={
                <Button type="primary" onClick={() => window.location.href = '/expedient'}>
                  Upgrade to Full Access
                </Button>
              }
            />
          </Col>
        </Row>
      </Content>
    </Layout>
    );
  }

  // Full dashboard for subscribed users
  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <Content style={{ padding: '24px' }}>
        {!savings && savingsUnavailable && (
          <Alert
            message="No verified savings figures yet"
            description={savingsUnavailable}
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        )}
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={2} style={{ margin: 0 }}>
              Value Dashboard
            </Title>
            <Text type="secondary">
              {city && state ? `${city}, ${state}` : 'Your Location'} • {utility || 'Local Utility'}
              {savings?.lastUpdated && (
                <span style={{ marginLeft: '8px' }}>
                  <DataFreshnessIndicator 
                    lastUpdated={savings.lastUpdated} 
                    source={savings.source}
                    staleThresholdMinutes={60}
                  />
                </span>
              )}
            </Text>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <Select
              value={period}
              onChange={setPeriod}
              style={{ width: 120 }}
              placeholder="Period"
            >
              <Option value="7">7 days</Option>
              <Option value="30">30 days</Option>
              <Option value="90">90 days</Option>
            </Select>
            {isSubscribed && <ExportMenu days={parseInt(period)} />}
          </div>
        </div>

        <Row gutter={[24, 24]}>
          {/* Key Metrics */}
          <Col span={24}>
            <Card title="💰 Financial Impact" style={{ marginBottom: '24px' }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Total Savings"
                    value={displayValue(savings?.dollarsSaved)}
                    prefix={<DollarOutlined />}
                    suffix={`/ ${period} days`}
                    valueStyle={{ color: '#3f8600' }}
                    formatter={(value) => (typeof value === 'number' ? formatCurrency(value) : '—')}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Annual Projection"
                    value={displayValue(annualizedSavings)}
                    prefix={<ArrowUpOutlined />}
                    valueStyle={{ color: '#3f8600' }}
                    formatter={(value) => (typeof value === 'number' ? formatCurrency(value) : '—')}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="ROI"
                    value={displayValue(savings?.roi)}
                    suffix="%"
                    prefix={<TrophyOutlined />}
                    valueStyle={{ color: savings?.roi && savings.roi > 100 ? '#3f8600' : '#1890ff' }}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Payback Period"
                    value={displayValue(savings?.paybackMonths)}
                    suffix="months"
                    prefix={<CalendarOutlined />}
                    valueStyle={{ color: savings?.paybackMonths && savings.paybackMonths < 12 ? '#3f8600' : '#1890ff' }}
                  />
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Energy Metrics */}
          <Col xs={24} lg={12}>
            <Card title="⚡ Energy Impact">
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Statistic
                    title="Energy Reduced"
                    value={displayValue(savings?.kWhReduced)}
                    suffix="kWh"
                    prefix={<ThunderboltOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Peak Shifted"
                    value={displayValue(savings?.kWShifted)}
                    suffix="kW"
                    prefix={<ArrowDownOutlined />}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Col>
                <Col span={24}>
                  <Statistic
                    title="CO₂ Reduced"
                    value={displayValue(savings?.co2Reduced)}
                    suffix="tCO₂e"
                    prefix={<EnvironmentOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Optimization Score */}
          <Col xs={24} lg={12}>
            <Card title="📊 Optimization Score">
              <div style={{ textAlign: 'center' }}>
                <Progress
                  type="circle"
                  percent={optScore ?? 0}
                  size={120}
                  strokeColor={
                    (optScore ?? -1) > 80
                      ? '#52c41a'
                      : (optScore ?? -1) > 60
                      ? '#1890ff'
                      : '#faad14'
                  }
                />
                <div style={{ marginTop: '16px' }}>
                  <Text strong>Overall Performance</Text>
                  <br />
                  <Text type="secondary">
                    {(optScore ?? -1) > 80
                      ? 'Excellent optimization'
                      : (optScore ?? -1) > 60
                      ? 'Good performance'
                      : optScore === null ? 'Awaiting metered data' : 'Room for improvement'}
                  </Text>
                </div>
              </div>
            </Card>
          </Col>

          {/* Energy Insights */}
          <Col xs={24} lg={12}>
            <Card title="⚡ Energy Insights">
              <List
                dataSource={insights?.energyInsights || []}
                renderItem={(insight) => (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>{insight.title}</span>
                          <Tag color={getPriorityColor(insight.priority)}>
                            {insight.priority.toUpperCase()}
                          </Tag>
                        </div>
                      }
                      description={
                        <div>
                          <div>{insight.description}</div>
                          <Text strong style={{ color: '#1890ff' }}>
                            {insight.value} {insight.unit}
                          </Text>
                          {insight.potentialSavings && (
                            <div style={{ marginTop: '4px' }}>
                              <Text type="success">
                                💰 Potential: {formatCurrency(insight.potentialSavings)}/month
                              </Text>
                            </div>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>

          {/* Recommendations */}
          <Col xs={24} lg={12}>
            <Card 
              title="🎯 Action Items" 
              extra={
                <Text type="secondary">
                  Potential: {insights?.totalPotentialSavings != null ? formatCurrency(insights.totalPotentialSavings) + '/month' : '—'}
                </Text>
              }
            >
              <List
                dataSource={insights?.recommendations || []}
                renderItem={(recommendation) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<BulbOutlined style={{ color: '#faad14' }} />}
                      description={recommendation}
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>

          {/* Location Context */}
          <Col span={24}>
            <Alert
              message={`Location-Optimized for ${city || 'Your Area'}`}
              description={`All metrics are calculated for your specific location, utility rates (${utility || 'local utility'}), and regional factors. Savings estimates include time-of-use rates, demand charges, and available incentive programs.`}
              type="info"
              showIcon
              icon={<EnvironmentOutlined />}
            />
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export default ValueDashboard;
